import React, { memo, useCallback, useMemo, useState } from 'react';
import { useDispatch, useSelector } from 'react-redux';

import { useStandaloneRunnerContext } from 'app/pages/loanApplication/LoanHeaders/LoanInfoHeader/LoanActionsControls';
import LoanActionRunnerProvider from 'app/pages/loanApplication/LoanHeaders/LoanInfoHeader/LoanActionsControls/LoanActionRunnerProvider';
import { isSendFormBundleAction } from 'app/pages/loanApplication/LoanHeaders/RedesignedLoanInfoHeader/LoanActionsControls/LoanActionsControls.utils';
import { loanActionApiV2 } from 'app/services/api';
import PropTypes from 'prop-types';

import { LoanEditorActionsEnum } from 'constants/loanActionEditor';
import { getDefaultErrorMessage } from 'utils/axios';
import { findMersPreviewResult } from 'utils/loanActions';
import {
  buildTaskActionSource,
  checkHasTaskActionItemType,
  checkIsTaskActionSupported,
  getTaskActionExecuteRequest,
  getTaskCompletionSkippedResult,
} from 'utils/taskTypeActions';

import { selectIsSubCompanyUser } from 'store/auth/selectors';
import { showErrorMessageAction, showMessageAction } from 'store/global/actions';
import { selectTaskTypeById } from 'store/system/selectors';

import useTaskActionFormBundles from './hooks/useTaskActionFormBundles';
import useTaskTypeAction from './hooks/useTaskTypeAction';
import { EMPTY_TASK_ACTION_SOURCE, TaskActionRunnerContext } from './TaskActionRunner.constants';

/**
 * Hosts a task's action run flow (the shared loan-action runner + its drawers) ABOVE the task
 * details, so a drawer or prompt opened by the action survives the task refreshing, remounting or
 * the button hiding after completion. It keeps the same element shape whether or not the task
 * supports an action, so loading the flag / task type never remounts the task details. Once a run
 * completes, a configured completion hides the button even if the host still shows the old task. TaskActionButton inside `children` reads it through
 * useTaskActionRunner. Loan-editor hosts pass their `runnerContext` and keep the header's post-run
 * page reload; `isStandalone` hosts (the task pipeline modal) get a context loaded for the task's
 * loan once the task type turns out to have an action, and refresh in place (`onTaskRefresh`)
 * instead of reloading. `onTaskRefresh` also runs after a browser-only branch records completion.
 */
const TaskActionRunner = ({
  task = null,
  runnerContext = null,
  isStandalone = false,
  onTaskRefresh = null,
  children,
}) => {
  const dispatch = useDispatch();

  const [isClientLaunchPending, setIsClientLaunchPending] = useState(false);
  const [completedTaskId, setCompletedTaskId] = useState(null);

  const isSubCompanyUser = useSelector(selectIsSubCompanyUser);
  const taskTypeRelatedData = useSelector(selectTaskTypeById(task?.taskTypeId ?? null));

  const taskId = task?.taskId ?? null;
  const applicationId = task?.applicationId ?? null;
  const taskType = taskTypeRelatedData || task?.taskType || null;

  const isSupported =
    !!task &&
    checkIsTaskActionSupported({
      taskAssignmentType: taskType?.taskAssignmentType ?? null,
      applicationId: task.applicationId ?? null,
    });

  const loadedTaskTypeAction = useTaskTypeAction({
    taskTypeId: task?.taskTypeId ?? null,
    isEnabled: isSupported,
  });

  const taskTypeAction =
    loadedTaskTypeAction && !(isSubCompanyUser && isSendFormBundleAction(loadedTaskTypeAction))
      ? loadedTaskTypeAction
      : null;

  const { runnerContext: standaloneRunnerContext, isFailed } = useStandaloneRunnerContext({
    applicationId: task?.applicationId ?? null,
    loanId: task?.loanId || null,
    isEnabled: isStandalone && !!taskTypeAction,
  });

  const isFormBundlesReady = useTaskActionFormBundles({
    isEnabled:
      isStandalone &&
      checkHasTaskActionItemType(taskTypeAction, [LoanEditorActionsEnum.UPLOAD_FORM_DELIVERY]),
  });

  const effectiveRunnerContext = isStandalone ? standaloneRunnerContext : runnerContext;

  const actionSource = useMemo(
    () => (taskTypeAction ? buildTaskActionSource(taskTypeAction) : EMPTY_TASK_ACTION_SOURCE),
    [taskTypeAction],
  );

  const onTaskActionExecuted = useCallback(
    results => {
      // A preview never completes its task: the button stays and the expected skipped completion is not news.
      if (findMersPreviewResult(results)) {
        return;
      }

      const completionSkippedResult = getTaskCompletionSkippedResult(results);

      if (completionSkippedResult?.message) {
        dispatch(
          showMessageAction({ message: completionSkippedResult.message, variant: 'warning' }),
        );
      }

      if (!completionSkippedResult) {
        setCompletedTaskId(taskId);
      }
    },
    [dispatch, taskId],
  );

  const executor = useCallback(
    ({ metadata = null }) =>
      loanActionApiV2
        .executeStandaloneActions(
          getTaskActionExecuteRequest({ taskId, applicationId, taskTypeAction, metadata }),
        )
        .then(response => {
          onTaskActionExecuted(response?.data);

          return response;
        }),
    [applicationId, onTaskActionExecuted, taskId, taskTypeAction],
  );

  const onClientOnlyLaunch = useCallback(() => {
    setIsClientLaunchPending(true);

    return loanActionApiV2
      .executeStandaloneActions(
        getTaskActionExecuteRequest({ taskId, applicationId, taskTypeAction }),
      )
      .then(({ data }) => {
        onTaskActionExecuted(data);
        onTaskRefresh?.(data);
      })
      .catch(err => dispatch(showErrorMessageAction({ message: getDefaultErrorMessage(err), err })))
      .finally(() => setIsClientLaunchPending(false));
  }, [applicationId, dispatch, onTaskActionExecuted, onTaskRefresh, taskId, taskTypeAction]);

  const value = useMemo(
    () =>
      isSupported
        ? {
            taskTypeAction,
            taskDataType: taskType?.dataType ?? null,
            isReady: !!effectiveRunnerContext && isFormBundlesReady,
            isLoadFailed: isStandalone && isFailed,
            isPending: isClientLaunchPending,
            isCompleted: !!taskId && completedTaskId === taskId,
          }
        : null,
    [
      isSupported,
      taskTypeAction,
      taskType?.dataType,
      effectiveRunnerContext,
      isFormBundlesReady,
      isStandalone,
      isFailed,
      isClientLaunchPending,
      taskId,
      completedTaskId,
    ],
  );

  return (
    <TaskActionRunnerContext.Provider value={value}>
      <LoanActionRunnerProvider
        actionSource={actionSource}
        executor={executor}
        isEnabled={isSupported}
        runnerContext={effectiveRunnerContext}
        onAfterExecute={isStandalone ? onTaskRefresh : null}
        onClientOnlyLaunch={onClientOnlyLaunch}
      >
        {children}
      </LoanActionRunnerProvider>
    </TaskActionRunnerContext.Provider>
  );
};

TaskActionRunner.propTypes = {
  children: PropTypes.node.isRequired,
  task: PropTypes.shape({
    taskId: PropTypes.string,
    taskTypeId: PropTypes.string,
    applicationId: PropTypes.string,
    loanId: PropTypes.string,
    taskType: PropTypes.object,
  }),
  runnerContext: PropTypes.object,
  isStandalone: PropTypes.bool,
  onTaskRefresh: PropTypes.func,
};

export default memo(TaskActionRunner);
